import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  createSession,
  updateCompanyInfo,
  uploadCompanyLogo,
} from "../api/user";
import { useAuth } from "../contexts/AuthContext";
import PricingPlansDashboard from "./PricingPlansDashboard";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CloudArrowUpIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/axios";
import PhoneInput, { isValidPhone } from "./PhoneInput";
import { Dropdown, Input, Button, ProcessingOverlay } from "./ui";

// Step validation schemas
const orgStepSchema = Yup.object({
  orgName: Yup.string().required("Organization name is required"),
  payment_country: Yup.string()
    .oneOf(["US", "IN"], "Please select a valid business location")
    .required("Business location is required"),
});

const companyStepSchema = Yup.object({
  companyName: Yup.string().required("Company name is required"),
  address: Yup.string().required("Address is required"),
  phone: Yup.string()
    .required("Phone number is required")
    .test("phone", "Enter a valid phone number (6–15 digits)", isValidPhone),
});

const CompanyInfoModal = ({ isOpen, onClose, companyInfo }) => {
  const { user, setUser, logout } = useAuth();
  const [step, setStep] = useState(1); // 1 = org details, 2 = company details
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(false);

  const showLoader = () => setLoading(true);
  const hideLoader = () => setLoading(false);

  // Step 1 submit — save org data and move to step 2
  const handleOrgStepSubmit = (values) => {
    setOrgData(values);
    setStep(2);
  };

  const LOGO_MAX_SIZE = 500 * 1024;

  // Step 2 submit — combine with step 1 data and call API
  const handleCompanyStepSubmit = async (
    values,
    { setSubmitting, setFieldError },
  ) => {
    try {
      const response = await updateCompanyInfo({
        orgName: orgData.orgName,
        payment_country: orgData.payment_country,
        companyInfo: {
          name: values.companyName,
          address: values.address,
          phone: values.phone,
        },
      });

      const company = response?.data?.company || response?.company;
      if (values.logoFile && company?._id) {
        try {
          await uploadCompanyLogo(company._id, values.logoFile);
        } catch (e) {
          console.warn("Logo upload failed:", e);
        }
      }

      const updatedUser = { ...user, companyInfo: company };
      localStorage.setItem("signing_user", JSON.stringify(updatedUser));
      setUser(updatedUser);

      window.location.reload();
      onClose();
    } catch (error) {
      console.error("Error updating company info:", error);
      setFieldError(
        "submit",
        error.response?.data?.message || "Failed to update company information",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const proceedRazorpaySubscription = async (subscriptionId, razorpayKeyId) => {
    const res = await loadRazorpay();
    if (!res) {
      alert("Razorpay SDK failed to load");
      return;
    }
    hideLoader();
    const options = {
      key: razorpayKeyId,
      subscription_id: subscriptionId,
      name: "EzSignly",
      description: "EzSignly Subscription",
      modal: { ondismiss: () => hideLoader() },
      handler: async (response) => {
        try {
          // Send payment ID to backend for verification
          const paymentId = response.razorpay_payment_id;
          console.log("Payment ID:", paymentId);

          const verifyResponse = await api.post(
            "/stripe/razorpay/set-payment",
            {
              paymentId,
            },
          );

          if (verifyResponse.data.status === "CAPTURED") {
            console.log("✓ Payment captured successfully");
          } else {
            console.log("Payment status:", verifyResponse.data.status);
          }
        } catch (error) {
          console.error("Payment verification error:", error);
        }
        hideLoader();
        window.location.reload();
      },
      prefill: {
        name: user?.name,
        email: user?.email,
      },
      theme: {
        color: "#1A6EDB",
      },
      config: {
        display: {
          blocks: {
            banks: {
              name: "Pay using",
              instruments: [
                { method: "upi" },
                { method: "card" },
                { method: "wallet" },
              ],
            },
          },
          sequence: ["block.banks"],
          preferences: {
            show_default_blocks: false,
          },
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handlePlanSelection = async (planId, interval) => {
    try {
      showLoader();
      const response = await createSession({
        planId,
        subscriptionType: interval.toUpperCase(),
        memberCount: 1,
        trialEnabled: true,
      });
      if (response.url) {
        window.location.href = response.url;
      } else if (
        response.paymentGateway === "RAZORPAY" &&
        response.subscriptionId
      ) {
        proceedRazorpaySubscription(
          response.subscriptionId,
          response.razorpayKeyId,
        );
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      window.document.body.style.overflow = "hidden";
    } else {
      window.document.body.style.overflow = "auto";
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Logo and Logout — absolute on both sides */}
      <Link
        to="/"
        className="absolute left-4 sm:left-6 lg:left-8 top-4 sm:top-6 z-10"
      >
        <img
          src="/Logo_EzSignly.png"
          alt="EzSignly Logo"
          className="h-auto w-[150px]"
        />
      </Link>
      <Button
        type="button"
        variant="ghost"
        onClick={handleLogout}
        className="absolute right-4 sm:right-6 lg:right-8 top-4 sm:top-6 z-10 py-2.5 flex items-center gap-2"
      >
        Logout
        <ArrowRightIcon className="w-4 h-4" />
      </Button>

      {/* Main */}
      <div className="flex-1 overflow-y-auto flex items-start justify-center p-4">
        {/* ── STEP 1 & 2: Profile setup (only shown when company not yet set up) ── */}
        {!companyInfo && (
          <div className="w-full max-w-lg p-6">
            <div className="text-center mb-6">
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-1">
                Complete Your Profile
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {step === 1
                  ? "Tell us about your organization first."
                  : "Now add your first company details."}
              </p>

              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mt-5">
                {[1, 2].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors duration-200 ${
                        s === step
                          ? "bg-primary border-primary text-white"
                          : s < step
                            ? "bg-primary/20 border-primary text-primary"
                            : "bg-gray-100 border-gray-300 text-gray-400"
                      }`}
                    >
                      {s < step ? "✓" : s}
                    </div>
                    {s < 2 && <div className="w-10 h-0.5 bg-gray-200" />}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Step 1: Organization ── */}
            {step === 1 && (
              <Formik
                initialValues={{
                  orgName: orgData?.orgName || "",
                  payment_country: orgData?.payment_country || "",
                }}
                validationSchema={orgStepSchema}
                onSubmit={handleOrgStepSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  setFieldTouched,
                }) => (
                  <Form className="space-y-5">
                    <Input
                      id="orgName"
                      name="orgName"
                      type="text"
                      label="Organization Name"
                      required
                      value={values.orgName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. Acme Corp"
                      error={
                        errors.orgName && touched.orgName
                          ? errors.orgName
                          : undefined
                      }
                    />

                    <div>
                      <Dropdown
                        label="Business Location"
                        options={[
                          { value: "US", label: "Outside India (USD)" },
                          { value: "IN", label: "Within India (INR)" },
                        ]}
                        value={values.payment_country}
                        onChange={(val) =>
                          setFieldValue("payment_country", val)
                        }
                        onBlur={() => setFieldTouched("payment_country", true)}
                        placeholder="Select location"
                        error={
                          errors.payment_country && touched.payment_country
                            ? errors.payment_country
                            : undefined
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Determines your billing currency and payment gateway.
                      </p>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        fullWidth
                        className="py-3"
                        rightIcon={ArrowRightIcon}
                      >
                        Continue
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}

            {/* ── Step 2: Company ── */}
            {step === 2 && (
              <Formik
                initialValues={{
                  companyName: "",
                  address: "",
                  phone: "",
                  logoFile: null,
                  logoPreview: null,
                }}
                validationSchema={companyStepSchema}
                onSubmit={handleCompanyStepSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  isSubmitting,
                  setFieldValue,
                  setFieldError,
                }) => (
                  <Form className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Company Logo
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Optional · JPG, PNG, GIF or WebP · Max 500KB
                      </p>
                      <label className="block cursor-pointer group">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > LOGO_MAX_SIZE) {
                                setFieldError(
                                  "submit",
                                  "Logo must be 500KB or smaller",
                                );
                                return;
                              }
                              setFieldValue("logoFile", file);
                              setFieldValue(
                                "logoPreview",
                                URL.createObjectURL(file),
                              );
                              setFieldError("submit", undefined);
                            }
                          }}
                        />
                        {values.logoPreview ? (
                          <div className="relative w-32 h-32 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center group-hover:ring-2 group-hover:ring-primary/20 group-hover:border-primary transition-all duration-200">
                            <img
                              src={values.logoPreview}
                              alt="Company logo"
                              className="w-full h-full object-contain"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <CloudArrowUpIcon className="w-5 h-5 text-white" />
                              <span className="text-sm font-medium text-white">
                                Change
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFieldValue("logoFile", null);
                                setFieldValue("logoPreview", null);
                                setFieldError("submit", undefined);
                              }}
                              className="absolute top-1.5 right-1.5 p-1 rounded-lg bg-white/90 hover:bg-white shadow-sm text-gray-600 hover:text-red-600 transition-colors duration-200"
                              aria-label="Remove logo"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 flex flex-col items-center justify-center gap-1.5 text-center">
                            <CloudArrowUpIcon className="w-8 h-8 text-gray-400 group-hover:text-primary transition-colors duration-200" />
                            <span className="text-xs font-medium text-gray-600 group-hover:text-primary transition-colors duration-200">
                              Click to upload
                            </span>
                            <span className="text-[10px] text-gray-400">
                              JPG, PNG, GIF, WebP
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      label="Company Name"
                      required
                      value={values.companyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your company name"
                      error={
                        errors.companyName && touched.companyName
                          ? errors.companyName
                          : undefined
                      }
                    />

                    <Input
                      id="address"
                      name="address"
                      type="text"
                      label="Company Address"
                      required
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter company address"
                      error={
                        errors.address && touched.address
                          ? errors.address
                          : undefined
                      }
                    />

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Company Phone <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        id="phone"
                        name="phone"
                        value={values.phone}
                        onChange={(v) => setFieldValue("phone", v)}
                        onBlur={handleBlur}
                        required
                        error={errors.phone && touched.phone}
                      />
                      {errors.phone && touched.phone && (
                        <p className="mt-1.5 text-xs font-medium text-red-600">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center gap-2">
                        <InformationCircleIcon className="h-5 w-5 text-red-500 shrink-0" />
                        <p className="text-sm font-medium text-red-700">
                          {errors.submit}
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        fullWidth
                        onClick={() => setStep(1)}
                        className="py-3"
                        leftIcon={ArrowLeftIcon}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        fullWidth
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className="py-3"
                      >
                        Complete Profile
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        )}

        {/* ── Plan selection (after company is set up but not subscribed) ── */}
        {!companyInfo?.isSubscribed && companyInfo && (
          <div className="w-full max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            <div className="text-center mb-10">
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                Choose Your Plan
              </h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto mt-1">
                Select the perfect plan for your signing needs.
              </p>
            </div>
            <PricingPlansDashboard
              isMinimal={true}
              showHeader={false}
              showBillingToggle={true}
              onPlanSelect={handlePlanSelection}
            />
          </div>
        )}
      </div>

      <ProcessingOverlay
        isOpen={loading}
        message="Processing your payment…"
        hint="Do not close or refresh this browser tab."
      />
    </div>
  );
};

export default CompanyInfoModal;
